import React, { useState } from 'react';
import { 
    TextField, 
    Switch, 
    FormControlLabel, 
    Box, 
    Typography,
    Chip,
    Button,
    List,
    ListItem,
    IconButton,
    Checkbox,
    Tooltip,
    CircularProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { styled } from '@mui/material/styles';
import InfoIcon from '@mui/icons-material/Info';

interface CustomFieldProps {
    fieldDescription: string;
    isRequired: boolean;
    existingOptions?: string[];
    onFieldChange: (field: { 
        fieldDescription: string; 
        isRequired: boolean;
        options?: string[];
    }) => void;
    onSubmit?: (field: { 
        fieldDescription: string; 
        isRequired: boolean;
        options?: string[];
        type: string;
        optional?: boolean;
    }) => void;
    optional?: boolean;
}

// Add the AntSwitch definition
const AntSwitch = styled(Switch)(({ theme }) => ({
  width: 42,
  height: 24,
  padding: 0,
  display: 'flex',
  '&:active': {
    '& .MuiSwitch-thumb': {
      width: 22,
    },
    '& .MuiSwitch-switchBase.Mui-checked': {
      transform: 'translateX(13px)',
    },
  },
  '& .MuiSwitch-switchBase': {
    padding: 3,
    '&.Mui-checked': {
      transform: 'translateX(18px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: '#2563eb',
      },
    },
  },
  '& .MuiSwitch-thumb': {
    boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
    width: 18,
    height: 18,
    borderRadius: 9,
    transition: theme.transitions.create(['width'], {
      duration: 200,
    }),
  },
  '& .MuiSwitch-track': {
    borderRadius: 24 / 2,
    opacity: 1,
    backgroundColor: 'rgba(0,0,0,.25)',
    boxSizing: 'border-box',
  },
}));

// Text Field Component
export const TextFieldComponent: React.FC<CustomFieldProps> = ({ 
    fieldDescription, 
    isRequired, 
    onFieldChange,
    onSubmit,
    optional = false
}) => {
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    console.log(optional,"new optional");

    const handleSubmit = async () => {
        if (!fieldDescription.trim()) {
            setError('Please enter a question');
            return;
        }

        setError(null);
        setIsSubmitting(true);
        try {
            await onSubmit?.({
                fieldDescription,
                isRequired,
                type: 'textfield'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box className="space-y-6 p-4 bg-white rounded-xl transition-all duration-300">
            <div className="space-y-2">
                <Typography variant="h6" className="text-gray-800 font-semibold flex items-center gap-2">
                    <span className="p-1.5 bg-blue-50 rounded-lg">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" 
                             className="text-blue-600" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                    </span>
                    Text Field
                </Typography>
                <Typography variant="body2" className="text-gray-600">
                    Add a text field question that attendees can answer with free-form text.
                </Typography>
            </div>

            <TextField
                fullWidth
                label="Question"
                value={fieldDescription}
                onChange={(e) => onFieldChange({ fieldDescription: e.target.value, isRequired })}
                placeholder="e.g., What's your full name?"
                variant="outlined"
                className="transition-all duration-300 hover:shadow-md"
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        transition: 'all 0.3s',
                        '&:hover': {
                            backgroundColor: '#F9FAFB'
                        }
                    }
                }}
            />

            {error && (
                <Typography className="text-red-500 text-sm mt-2">
                    {error}
                </Typography>
            )}

            <Box className="space-y-4">
                {!optional && (
                <Box className="flex items-center space-x-4 bg-gray-50 px-4 py-2.5 rounded-xl hover:bg-gray-100 transition-colors duration-300">
                    <div className="flex items-center gap-2">
                        <Typography className="text-gray-700">
                            <p className='text-md text-black'>Required field</p>
                        </Typography>
                        <div className="relative group flex items-center">
                            
                            <Tooltip arrow title="Enabling this option will make this field mandatory. Attendees must provide a response to proceed with registration.">
                            <InfoIcon fontSize="small" className="text-gray-500" />
                            </Tooltip>
                            
                        </div>
                    </div>
                    <AntSwitch
                        checked={isRequired}
                        onChange={(e) => onFieldChange({ 
                            fieldDescription, 
                            isRequired: e.target.checked 
                        })}
                        inputProps={{ 'aria-label': 'required field' }}
                    />
                </Box>
                )}
                <Box className="flex justify-center">
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="bg-blue-600 hover:bg-blue-700 transition-all duration-300 w-1/2"
                        sx={{ 
                            borderRadius: '8px',
                            textTransform: 'none',
                            py: 1,
                            '&:hover': {
                                backgroundColor: '#1D4ED8'
                            }
                        }}
                    >
                        {isSubmitting ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            'Submit'
                        )}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

// Dropdown Component with Options Management
export const DropdownComponent: React.FC<CustomFieldProps> = ({ 
    fieldDescription, 
    isRequired,
    existingOptions,
    onFieldChange,
    onSubmit,
    optional = false
}) => {
    const [options, setOptions] = useState<string[]>(existingOptions || []);
    const [newOption, setNewOption] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleAddOption = () => {
        if (newOption.trim()) {
            const updatedOptions = [...options, newOption.trim()];
            setOptions(updatedOptions);
            setNewOption('');
            onFieldChange({ fieldDescription, isRequired, options: updatedOptions });
        }
    };

    const handleRemoveOption = (indexToRemove: number) => {
        const updatedOptions = options.filter((_, index) => index !== indexToRemove);
        setOptions(updatedOptions);
        onFieldChange({ fieldDescription, isRequired, options: updatedOptions });
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddOption();
        }
    };

    const handleSubmit = async () => {
        if (!fieldDescription.trim()) {
            setError('Please enter a question');
            return;
        }
        if (options.length < 2) {
            setError('Please add at least 2 options');
            return;
        }

        setError(null);
        setIsSubmitting(true);
        try {
            await onSubmit?.({
                fieldDescription,
                isRequired,
                options,
                type: 'dropdown'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box className="space-y-6 p-4 bg-white rounded-xl transition-all duration-300">
            <div className="space-y-2">
                <Typography variant="h6" className="text-gray-800 font-semibold flex items-center gap-2">
                    <span className="p-1.5 bg-purple-50 rounded-lg">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" 
                             className="text-purple-600" strokeWidth="2">
                            <path d="M6 9l6 6 6-6"/>
                        </svg>
                    </span>
                    Dropdown Selection
                </Typography>
                <Typography variant="body2" className="text-gray-600">
                    Create a dropdown list where attendees can select one option from your predefined choices.
                </Typography>
            </div>
            
            <TextField
                fullWidth
                label="Question"
                value={fieldDescription}
                onChange={(e) => onFieldChange({ 
                    fieldDescription: e.target.value, 
                    isRequired,
                    options 
                })}
                placeholder="e.g., What's your t-shirt size?"
                variant="outlined"
                className="transition-all duration-300 hover:shadow-md"
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        transition: 'all 0.3s',
                        '&:hover': {
                            backgroundColor: '#F9FAFB'
                        }
                    }
                }}
            />

            <Box className="space-y-3">
                <Typography variant="subtitle2" className="text-gray-700 font-semibold">
                    Add Options
                </Typography>
                <div className="flex gap-2">
                    <TextField
                        fullWidth
                        size="small"
                        value={newOption}
                        onChange={(e) => setNewOption(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Type an option and press Enter"
                        variant="outlined"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                                transition: 'all 0.3s',
                                '&:hover': {
                                    backgroundColor: '#F9FAFB'
                                }
                            }
                        }}
                    />
                    <Button 
                        variant="contained" 
                        onClick={handleAddOption}
                        disabled={!newOption.trim()}
                        className="min-w-[100px] bg-purple-600 hover:bg-purple-700 transition-all duration-300"
                        sx={{ borderRadius: '8px' }}
                    >
                        Add
                    </Button>
                </div>
            </Box>

            {options.length > 0 && (
                <List className="bg-gray-50 rounded-xl p-2">
                    {options.map((option, index) => (
                        <ListItem
                            key={index}
                            className="bg-white rounded-lg mb-2 shadow-sm hover:shadow-md transition-all duration-300"
                            secondaryAction={
                                <IconButton 
                                    edge="end" 
                                    aria-label="delete"
                                    onClick={() => handleRemoveOption(index)}
                                    className="hover:text-red-500 transition-colors duration-300"
                                >
                                    <DeleteIcon />
                                </IconButton>
                            }
                        >
                            <Chip 
                                label={option} 
                                variant="outlined" 
                                className="bg-white hover:bg-gray-50 transition-colors duration-300"
                            />
                        </ListItem>
                    ))}
                </List>
            )}

            {error && (
                <Typography className="text-red-500 text-sm mt-2">
                    {error}
                </Typography>
            )}

            <Box className="space-y-4">
                {!optional && (
                <Box className="flex items-center space-x-4 bg-gray-50 px-4 py-2.5 rounded-xl hover:bg-gray-100 transition-colors duration-300">
                    <div className="flex items-center gap-2">
                        <Typography className="text-gray-700">
                            <p className='text-md text-black'>Required field</p>
                        </Typography>
                        <div className="relative group flex items-center">
                            
                            <Tooltip arrow title="Enabling this option will make this field mandatory. Attendees must provide a response to proceed with registration.">
                            <InfoIcon fontSize="small" className="text-gray-500" />
                            </Tooltip>
                            
                        </div>
                    </div>
                    <AntSwitch
                        checked={isRequired}
                        onChange={(e) => onFieldChange({ 
                            fieldDescription, 
                            isRequired: e.target.checked,
                            options
                        })}
                        inputProps={{ 'aria-label': 'required field' }}
                    />
                </Box>
                )}
                <Box className="flex justify-center">
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="bg-blue-600 hover:bg-blue-700 transition-all duration-300 w-1/2"
                        sx={{ 
                            borderRadius: '8px',
                            textTransform: 'none',
                            py: 1,
                            '&:hover': {
                                backgroundColor: '#1D4ED8'
                            }
                        }}
                    >
                        {isSubmitting ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            'Submit'
                        )}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

// Radio Component with Options Management
export const RadioComponent: React.FC<CustomFieldProps> = ({ 
    fieldDescription, 
    isRequired, 
    existingOptions,
    onFieldChange,
    onSubmit,
    optional = false
}) => {
    const [options, setOptions] = useState<string[]>(existingOptions || []);
    const [newOption, setNewOption] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleAddOption = () => {
        if (newOption.trim() && options.length < 2) {
            const updatedOptions = [...options, newOption.trim()];
            setOptions(updatedOptions);
            setNewOption('');
            onFieldChange({ fieldDescription, isRequired, options: updatedOptions });
        }
    };

    const handleRemoveOption = (indexToRemove: number) => {
        const updatedOptions = options.filter((_, index) => index !== indexToRemove);
        setOptions(updatedOptions);
        onFieldChange({ fieldDescription, isRequired, options: updatedOptions });
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddOption();
        }
    };

    const handleSubmit = async () => {
        if (!fieldDescription.trim()) {
            setError('Please enter a question');
            return;
        }
        if (options.length !== 2) {
            setError('Please add exactly 2 options');
            return;
        }

        setError(null);
        setIsSubmitting(true);
        try {
            await onSubmit?.({
                fieldDescription,
                isRequired,
                options,
                type: 'radio'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box className="space-y-6 p-4 bg-white rounded-xl transition-all duration-300">
            <div className="space-y-2">
                <Typography variant="h6" className="text-gray-800 font-semibold flex items-center gap-2">
                    <span className="p-1.5 bg-amber-50 rounded-lg">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" 
                             className="text-amber-600" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <circle cx="12" cy="12" r="3"/>
                        </svg>
                    </span>
                    Binary Choice Question
                </Typography>
                <Typography variant="body2" className="text-gray-600">
                    Create a two-option choice where attendees must select exactly one option (e.g., Yes/No, Agree/Disagree).
                </Typography>
            </div>

            <TextField
                fullWidth
                label="Question"
                value={fieldDescription}
                onChange={(e) => onFieldChange({ 
                    fieldDescription: e.target.value, 
                    isRequired,
                    options 
                })}
                placeholder="e.g., Will you attend the evening session?"
                variant="outlined"
                className="transition-all duration-300 hover:shadow-md"
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        transition: 'all 0.3s',
                        '&:hover': {
                            backgroundColor: '#F9FAFB'
                        }
                    }
                }}
            />

            <Box className="space-y-3">
                <div className="flex justify-between items-center">
                    <Typography variant="subtitle2" className="text-gray-700 font-semibold">
                        Options
                    </Typography>
                    <span className="text-sm text-gray-500">
                        {options.length}/2 options added
                    </span>
                </div>
                <div className="flex gap-2">
                    <TextField
                        fullWidth
                        size="small"
                        value={newOption}
                        onChange={(e) => setNewOption(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder={options.length === 0 ? "First option (e.g., Yes)" : "Second option (e.g., No)"}
                        variant="outlined"
                        disabled={options.length >= 2}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                                transition: 'all 0.3s',
                                '&:hover': {
                                    backgroundColor: '#F9FAFB'
                                }
                            }
                        }}
                    />
                    <Button 
                        variant="contained" 
                        onClick={handleAddOption}
                        disabled={!newOption.trim() || options.length >= 2}
                        className="min-w-[100px] bg-amber-600 hover:bg-amber-700 transition-all duration-300"
                        sx={{ borderRadius: '8px' }}
                    >
                        Add
                    </Button>
                </div>
            </Box>

            {options.length > 0 && (
                <List className="bg-gray-50 rounded-xl p-2">
                    {options.map((option, index) => (
                        <ListItem
                            key={index}
                            className="bg-white rounded-lg mb-2 shadow-sm hover:shadow-md transition-all duration-300"
                            secondaryAction={
                                <IconButton 
                                    edge="end" 
                                    aria-label="delete"
                                    onClick={() => handleRemoveOption(index)}
                                    className="hover:text-red-500 transition-colors duration-300"
                                >
                                    <DeleteIcon />
                                </IconButton>
                            }
                        >
                            <Chip 
                                label={option} 
                                variant="outlined" 
                                className="bg-white hover:bg-gray-50 transition-colors duration-300"
                            />
                        </ListItem>
                    ))}
                </List>
            )}

            {error && (
                <Typography className="text-red-500 text-sm mt-2">
                    {error}
                </Typography>
            )}

            <Box className="space-y-4">
                {!optional && (
                <Box className="flex items-center space-x-4 bg-gray-50 px-4 py-2.5 rounded-xl hover:bg-gray-100 transition-colors duration-300">
                    <div className="flex items-center gap-2">
                        <Typography className="text-gray-700">
                            <p className='text-md text-black'>Required field</p>
                        </Typography>
                        <div className="relative group flex items-center">
                            
                            <Tooltip arrow title="Enabling this option will make this field mandatory. Attendees must provide a response to proceed with registration.">
                            <InfoIcon fontSize="small" className="text-gray-500" />
                            </Tooltip>
                            
                        </div>
                    </div>
                    <AntSwitch
                        checked={isRequired}
                        onChange={(e) => onFieldChange({ 
                            fieldDescription, 
                            isRequired: e.target.checked,
                            options
                        })}
                        inputProps={{ 'aria-label': 'required field' }}
                    />
                </Box>
                )}  
                <Box className="flex justify-center">
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="bg-blue-600 hover:bg-blue-700 transition-all duration-300 w-1/2"
                        sx={{ 
                            borderRadius: '8px',
                            textTransform: 'none',
                            py: 1,
                            '&:hover': {
                                backgroundColor: '#1D4ED8'
                            }
                        }}
                    >
                        {isSubmitting ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            'Submit'
                        )}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

// Similar components for Radio, Checkbox, and Location
export const RadioComponents: React.FC<CustomFieldProps> = ({ fieldDescription, isRequired, onFieldChange }) => {
    return (
        <Box className="space-y-4">
            <div className="mb-6">
                <Typography variant="body2" className="text-gray-600 mb-2">
                    Create radio buttons where attendees must select exactly one option.
                </Typography>
            </div>
            <TextField
                fullWidth
                label="Question"
                value={fieldDescription}
                onChange={(e) => onFieldChange({ fieldDescription: e.target.value, isRequired })}
                placeholder="Enter your question here"
            />
            <FormControlLabel
                control={
                    <Switch
                        checked={isRequired}
                        onChange={(e) => onFieldChange({ fieldDescription, isRequired: e.target.checked })}
                    />
                }
                label="Required field"
            />
        </Box>
    );
};

export const CheckboxComponent: React.FC<CustomFieldProps> = ({ 
    fieldDescription, 
    isRequired, 
    onFieldChange,
    onSubmit,
    optional = false
}) => {
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async () => {
        if (!fieldDescription.trim()) {
            setError('Please enter a statement');
            return;
        }

        setError(null);
        setIsSubmitting(true);
        try {
            await onSubmit?.({
                fieldDescription,
                isRequired,
                type: 'checkbox'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box className="space-y-6 p-4 bg-white rounded-xl transition-all duration-300">
            <div className="space-y-2">
                <Typography variant="h6" className="text-gray-800 font-semibold flex items-center gap-2">
                    <span className="p-1.5 bg-green-50 rounded-lg">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" 
                             className="text-green-600" strokeWidth="2">
                            <polyline points="9 11 12 14 22 4"/>
                            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
                        </svg>
                    </span>
                    Checkbox Question
                </Typography>
                <Typography variant="body2" className="text-gray-600">
                    Add a single checkbox that attendees can mark. Useful for agreements, confirmations, or opt-ins.
                </Typography>
            </div>

            <TextField
                fullWidth
                label="Statement"
                value={fieldDescription}
                onChange={(e) => onFieldChange({ 
                    fieldDescription: e.target.value, 
                    isRequired 
                })}
                placeholder="e.g., I agree to the terms and conditions"
                variant="outlined"
                className="transition-all duration-300 hover:shadow-md"
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        transition: 'all 0.3s',
                        '&:hover': {
                            backgroundColor: '#F9FAFB'
                        }
                    }
                }}
            />

            <Box className="mt-4 p-4 bg-gray-50 rounded-xl hover:shadow-md transition-all duration-300">
                <Typography variant="subtitle2" className="text-gray-600 mb-2">
                    Preview:
                </Typography>
                <FormControlLabel
                    control={<Checkbox disabled />}
                    label={fieldDescription || "Your statement will appear here"}
                    className="text-gray-800"
                />
            </Box>

            {error && (
                <Typography className="text-red-500 text-sm mt-2">
                    {error}
                </Typography>
            )}

            <Box className="space-y-4">
                {!optional && (
                <Box className="flex items-center space-x-4 bg-gray-50 px-4 py-2.5 rounded-xl hover:bg-gray-100 transition-colors duration-300">
                    <div className="flex items-center gap-2">
                        <Typography className="text-gray-700">
                            <p className='text-md text-black'>Required field</p>
                        </Typography>
                        <div className="relative group flex items-center">
                            
                            <Tooltip arrow title="Enabling this option will make this field mandatory. Attendees must provide a response to proceed with registration.">
                            <InfoIcon fontSize="small" className="text-gray-500" />
                            </Tooltip>
                            
                        </div>
                    </div>
                    <AntSwitch
                        checked={isRequired}
                        onChange={(e) => onFieldChange({ 
                            fieldDescription, 
                            isRequired: e.target.checked 
                        })}
                        inputProps={{ 'aria-label': 'required field' }}
                    />
                </Box>
                )}
                <Box className="flex justify-center">
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="bg-blue-600 hover:bg-blue-700 transition-all duration-300 w-1/2"
                        sx={{ 
                            borderRadius: '8px',
                            textTransform: 'none',
                            py: 1,
                            '&:hover': {
                                backgroundColor: '#1D4ED8'
                            }
                        }}
                    >
                        {isSubmitting ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            'Submit'
                        )}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

// Location Component
export const LocationComponent: React.FC<CustomFieldProps> = ({ 
    fieldDescription, 
    isRequired, 
    onFieldChange,
    onSubmit,
    optional = false
}) => {
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async () => {
        if (!fieldDescription.trim()) {
            setError('Please enter a location question');
            return;
        }

        setError(null);
        setIsSubmitting(true);
        try {
            await onSubmit?.({
                fieldDescription,
                isRequired,
                type: 'location'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box className="space-y-6 p-4 bg-white rounded-xl transition-all duration-300">
            <div className="space-y-2">
                <Typography variant="h6" className="text-gray-800 font-semibold flex items-center gap-2">
                    <span className="p-1.5 bg-indigo-50 rounded-lg">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" 
                             className="text-indigo-600" strokeWidth="2">
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                            <circle cx="12" cy="10" r="3" />
                        </svg>
                    </span>
                    Location Question
                </Typography>
                <Typography variant="body2" className="text-gray-600">
                    Add a location field to collect address information from attendees.
                </Typography>
            </div>

            <TextField
                fullWidth
                label="Location Question"
                value={fieldDescription}
                onChange={(e) => onFieldChange({ 
                    fieldDescription: e.target.value, 
                    isRequired 
                })}
                placeholder="e.g., What's your pickup location?"
                variant="outlined"
                className="transition-all duration-300 hover:shadow-md"
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        transition: 'all 0.3s',
                        '&:hover': {
                            backgroundColor: '#F9FAFB'
                        }
                    }
                }}
            />

            <Box className="p-4 bg-indigo-50/50 rounded-xl border border-indigo-100">
                <div className="flex items-start gap-3">
                    <div className="p-2 bg-indigo-100 rounded-lg mt-0.5">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" 
                             className="text-indigo-600" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="16" x2="12" y2="12"></line>
                            <line x1="12" y1="8" x2="12.01" y2="8"></line>
                        </svg>
                    </div>
                    <div className="space-y-1">
                        <Typography variant="subtitle2" className="text-indigo-900 font-medium">
                            How it works
                        </Typography>
                        <Typography variant="body2" className="text-indigo-800/70">
                            Attendees will be prompted to enter their location using Google Maps. 
                            They can either type an address or select their location on the map.
                        </Typography>
                    </div>
                </div>
            </Box>

            {error && (
                <Typography className="text-red-500 text-sm mt-2">
                    {error}
                </Typography>
            )}

            <Box className="space-y-4">
                {!optional && (
                <Box className="flex items-center space-x-4 bg-gray-50 px-4 py-2.5 rounded-xl hover:bg-gray-100 transition-colors duration-300">
                    <div className="flex items-center gap-2">
                        <Typography className="text-gray-700">
                            <p className='text-md text-black'>Required field</p>
                        </Typography>
                        <div className="relative group flex items-center">
                            
                            <Tooltip arrow title="Enabling this option will make this field mandatory. Attendees must provide a response to proceed with registration.">
                            <InfoIcon fontSize="small" className="text-gray-500" />
                            </Tooltip>
                            
                        </div>
                    </div>
                    <AntSwitch
                        checked={isRequired}
                        onChange={(e) => onFieldChange({ 
                            fieldDescription, 
                            isRequired: e.target.checked 
                        })}
                        inputProps={{ 'aria-label': 'required field' }}
                    />
                </Box>
                )}  
                <Box className="flex justify-center">
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="bg-blue-600 hover:bg-blue-700 transition-all duration-300 w-1/2"
                        sx={{ 
                            borderRadius: '8px',
                            textTransform: 'none',
                            py: 1,
                            '&:hover': {
                                backgroundColor: '#1D4ED8'
                            }
                        }}
                    >
                        {isSubmitting ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            'Submit'
                        )}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

// File Upload Component
export const FileUploadComponent: React.FC<CustomFieldProps> = ({ 
    fieldDescription, 
    isRequired, 
    onFieldChange,
    onSubmit,
    optional = false
}) => {
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async () => {
        if (!fieldDescription.trim()) {
            setError('Please enter a file upload question');
            return;
        }

        setError(null);
        setIsSubmitting(true);
        try {
            await onSubmit?.({
                fieldDescription,
                isRequired,
                type: 'fileUpload'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box className="space-y-6 p-4 bg-white rounded-xl transition-all duration-300">
            <div className="space-y-2">
                <Typography variant="h6" className="text-gray-800 font-semibold flex items-center gap-2">
                    <span className="p-1.5 bg-orange-50 rounded-lg">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" 
                             className="text-orange-600" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="7,10 12,15 17,10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                    </span>
                    File Upload Question
                </Typography>
                <Typography variant="body2" className="text-gray-600">
                    Add a file upload field where attendees can upload documents, images, or other files.
                </Typography>
            </div>

            <TextField
                fullWidth
                label="File Upload Question"
                value={fieldDescription}
                onChange={(e) => onFieldChange({ 
                    fieldDescription: e.target.value, 
                    isRequired 
                })}
                placeholder="e.g., Please upload your ID document"
                variant="outlined"
                className="transition-all duration-300 hover:shadow-md"
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        transition: 'all 0.3s',
                        '&:hover': {
                            backgroundColor: '#F9FAFB'
                        }
                    }
                }}
            />

            <Box className="p-4 bg-orange-50/50 rounded-xl border border-orange-100">
                <div className="flex items-start gap-3">
                    <div className="p-2 bg-orange-100 rounded-lg mt-0.5">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" 
                             className="text-orange-600" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="16" x2="12" y2="12"></line>
                            <line x1="12" y1="8" x2="12.01" y2="8"></line>
                        </svg>
                    </div>
                    <div className="space-y-1">
                        <Typography variant="subtitle2" className="text-orange-900 font-medium">
                            File Upload Features
                        </Typography>
                        <Typography variant="body2" className="text-orange-800/70">
                            Attendees will be able to upload files with common formats (PDF, DOC, images, etc.). 
                            File size limits and accepted formats can be configured.
                        </Typography>
                    </div>
                </div>
            </Box>

            {error && (
                <Typography className="text-red-500 text-sm mt-2">
                    {error}
                </Typography>
            )}

            <Box className="space-y-4">
                {!optional && (
                <Box className="flex items-center space-x-4 bg-gray-50 px-4 py-2.5 rounded-xl hover:bg-gray-100 transition-colors duration-300">
                    <div className="flex items-center gap-2">
                        <Typography className="text-gray-700">
                            <p className='text-md text-black'>Required field</p>
                        </Typography>
                        <div className="relative group flex items-center">
                            
                            <Tooltip arrow title="Enabling this option will make this field mandatory. Attendees must upload a file to proceed with registration.">
                            <InfoIcon fontSize="small" className="text-gray-500" />
                            </Tooltip>
                            
                        </div>
                    </div>
                    <AntSwitch
                        checked={isRequired}
                        onChange={(e) => onFieldChange({ 
                            fieldDescription, 
                            isRequired: e.target.checked 
                        })}
                        inputProps={{ 'aria-label': 'required field' }}
                    />
                </Box>
                )}  
                <Box className="flex justify-center">
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="bg-blue-600 hover:bg-blue-700 transition-all duration-300 w-1/2"
                        sx={{ 
                            borderRadius: '8px',
                            textTransform: 'none',
                            py: 1,
                            '&:hover': {
                                backgroundColor: '#1D4ED8'
                            }
                        }}
                    >
                        {isSubmitting ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            'Submit'
                        )}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

// Add other components as needed...
